import type { SalesRegion } from '../../../api/daily-sales';

import type { SourceKey } from './daily-sales-format';

export type SaleType = 'tickets' | 'gift-cards' | 'retail-in-store' | 'retail-online';

export const SALE_TYPES: ReadonlyArray<{
  value: SaleType;
  label: string;
  sources: SourceKey[];
}> = [
  { value: 'tickets', label: 'Workshop tickets', sources: ['fareharbor', 'nebari'] },
  { value: 'gift-cards', label: 'Gift cards & passes', sources: ['giftCards', 'giftPasses'] },
  { value: 'retail-in-store', label: 'Retail in store', sources: ['retailInStore'] },
  { value: 'retail-online', label: 'Retail online', sources: ['retailOnline'] },
];

const ALL_TYPES = SALE_TYPES.map((t) => t.value);

export const TICKET_SOURCES: SourceKey[] = ['fareharbor', 'nebari'];
export const RETAIL_SOURCES: SourceKey[] = ['retailInStore', 'retailOnline'];

/** The sale types named in `?types=`, in the page's order. All of them when the address
 *  names none, or names only ones that do not exist. */
export function typesFromParams(params: URLSearchParams): SaleType[] {
  const named = new Set(params.get('types')?.split(',') ?? []);
  const types = ALL_TYPES.filter((t) => named.has(t));
  return types.length ? types : ALL_TYPES;
}

/** `?types=` for the chosen types, or null when every type is chosen. */
export function typesParam(types: SaleType[]): string | null {
  return types.length === ALL_TYPES.length ? null : types.join(',');
}

/** Choosing or unchoosing one type. The last chosen type cannot be unchosen. */
export function toggleType(types: SaleType[], type: SaleType): SaleType[] {
  if (!types.includes(type)) return ALL_TYPES.filter((t) => t === type || types.includes(t));
  return types.length > 1 ? types.filter((t) => t !== type) : types;
}

/**
 * The sources the page shows. Gift cards, gift passes and retail belong to no region, so a chosen
 * region narrows the page to workshop tickets whatever types are chosen.
 */
export function visibleSources(types: SaleType[], regionChosen: boolean): SourceKey[] {
  if (regionChosen) return TICKET_SOURCES;
  return SALE_TYPES.filter((t) => types.includes(t.value)).flatMap((t) => t.sources);
}

export function includesAny(sources: SourceKey[], wanted: SourceKey[]): boolean {
  return wanted.some((s) => sources.includes(s));
}

// Non-breaking spaces, because a select option collapses ordinary ones.
const INDENT = '\u00a0'.repeat(3);

/** Every region, each top-level one followed by the regions inside it, indented. */
export function regionChoices(regions: SalesRegion[]): Array<{ id: string; label: string }> {
  const byName = (a: SalesRegion, b: SalesRegion) => a.name.localeCompare(b.name);
  const ids = new Set(regions.map((r) => r.id));
  const roots = regions.filter((r) => !r.parentId || !ids.has(r.parentId)).sort(byName);
  return roots.flatMap((root) => [
    { id: root.id, label: root.name },
    ...regions
      .filter((r) => r.parentId === root.id)
      .sort(byName)
      .map((r) => ({ id: r.id, label: `${INDENT}${r.name}` })),
  ]);
}
